// Private dossier files on the acceptance page (Stage 1 slice 5). Plain module with no Vue and no
// imports, so it runs under `node --test`.
//
//   const blobs = createBlobCache({ load: getFileBlob, revoke: (url) => URL.revokeObjectURL(url) });
//   blobs.get(fileId) -> Promise<objectUrl>   one request per file; a failed load is retried
//   blobs.revoke(fileId)                      after the file is removed
//   blobs.revokeAll()                         on unmount and before another dossier loads
//   checkUpload(file, purpose) -> null | "too_large" | "unsupported_type" | "photo_must_be_image"
//   canRemoveFile(file, { role, userId, status })  cosmetic; the API decides (403 not_uploader,
//                                             403 not_manager after confirm)
//
// A file's bytes never change, so a cached object URL stays valid until revoked.

// Same limits as the API (core/compliance/files.js); the API checks again.
export const FILE_MAX_BYTES = 15 * 1024 * 1024;
// Per photo slot and per checklist line (the API refuses a sixth with 422 file_limit_reached).
export const MAX_FILES_PER_TARGET = 5;
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const FILE_TYPES = [...IMAGE_TYPES, "application/pdf"];

export function checkUpload(file, purpose) {
  if (!file) return "unsupported_type";
  if (file.size > FILE_MAX_BYTES) return "too_large";
  if (!FILE_TYPES.includes(file.type)) return "unsupported_type";
  if (purpose === "photo" && !IMAGE_TYPES.includes(file.type)) {
    return "photo_must_be_image";
  }
  return null;
}

// Same rule as the API: a manager removes any file; a User only their own while the acceptance is a
// draft. After confirm only a manager may (and must give a reason).
export const REMOVE_ANY_ROLES = ["InstitutionManager", "InstitutionAdmin"];

export function canRemoveFile(file, { role, userId, status } = {}) {
  if (!file) return false;
  if (REMOVE_ANY_ROLES.includes(role)) return true;
  if (status === "confirmed") return false;
  const uploader = file.uploadedBy && file.uploadedBy.userId;
  return Boolean(uploader && userId && String(uploader) === String(userId));
}

export function createBlobCache({ load, revoke }) {
  let entries = new Map(); // fileId -> Promise<objectUrl>
  let generation = 0;

  function get(fileId) {
    if (!entries.has(fileId)) {
      const started = generation;
      const promise = load(fileId).then((url) => {
        // Loaded after revokeAll: nobody will revoke it later, so do it now.
        if (started !== generation) {
          revoke(url);
          const error = new Error("revoked");
          error.code = "revoked";
          throw error;
        }
        return url;
      });
      promise.catch(() => {
        if (entries.get(fileId) === promise) entries.delete(fileId);
      });
      entries.set(fileId, promise);
    }
    return entries.get(fileId);
  }

  function revokeAll() {
    generation += 1;
    entries.forEach((promise) => {
      promise.then(revoke, () => {});
    });
    entries = new Map();
  }

  // Also covers a load still running: its URL is revoked once it arrives.
  function revokeOne(fileId) {
    const promise = entries.get(fileId);
    if (!promise) return;
    entries.delete(fileId);
    promise.then(revoke, () => {});
  }

  return { get, revoke: revokeOne, revokeAll };
}
